import React, { useState, useEffect } from "react";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select/creatable";
import {
  userFinishedAssessmentThunk,
  userSetsSectionThunk,
} from "../../store/thunks";
import { useKeyTerms } from "../../hooks/useKeyTerms";
import affiliates from "./affiliates";
import { trackAdobeClick } from "../../utils/adobeLogging";
import { useTranslation } from "react-i18next";

const Assessment = () => {
  useKeyTerms();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isFinished, setIsFinished] = useState(false);
  const history = useHistory();
  const assessment = useSelector(
    (state) => state.languages[state.ui.lang].assessment
  );
  const affiliate = useSelector((state) => state.affiliate);

  const SECTIONS = {
    intro: "intro",
    question1: "question1",
    question2: "question2",
    question3: "question3",
    question4: "question4",
    question5: "question5",
    question6: "question6",
    result1: "result1",
    result2: "result2",
  };

  const [currentSection, setCurrentSection] = useState(SECTIONS.intro);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(0);
  const [tempAffiliate, setTempAffiliate] = useState("");
  const [stack, setStack] = useState([]);

  const currentIndex = () => {
    if (currentSection === SECTIONS.intro) return 0;
    if (currentSection === SECTIONS.question1) return 1;
    if (currentSection === SECTIONS.question2) return 2;
    if (currentSection === SECTIONS.question3) return 3;
    if (currentSection === SECTIONS.question4) return 4;
    if (currentSection === SECTIONS.question5) return 5;
    if (currentSection === SECTIONS.question6) return 6;
    return 7;
  };

  const getQuestionBySection = () => {
    return assessment.questions[currentIndex() - 1];
  };
  const getAnswerForSection = () => {
    return answers[getQuestionBySection().id];
  };

  const isSelected = (q, a) => {
    const answer = answers[q.id];
    if (!answer) return false;
    return answer.answer.id === a.id;
  };

  const startOver = () => {
    trackAdobeClick(null, {
      linkTitle: "Start Over",
      linkModule: "kickoff-assessment",
      targetURL: "/app/activity/assessment",
    });
    setCurrentSection(SECTIONS.intro);
    setAnswers({});
    setStack([]);
  };

  const goNext = () => {
    const question = getQuestionBySection();
    const answer = getAnswerForSection();

    if (!question || !answer) return;

    setStack([...stack, currentSection]);

    if (question.id === SECTIONS.question1)
      setCurrentSection(SECTIONS.question2);
    if (question.id === SECTIONS.question2) {
      if (
        answers.question1.answer.id === "no" ||
        answers.question1.answer.id === "unsure"
      ) {
        if (answer.id === "software" || answer.id === "marketing") {
          setResult(2);
        } else {
          setResult(1);
        }
        setCurrentSection(SECTIONS.question5);
      } else {
        setCurrentSection(SECTIONS.question3);
      }
    }
    if (question.id === SECTIONS.question3)
      setCurrentSection(SECTIONS.question4);
    if (question.id === SECTIONS.question4) {
      if (answers.question3.answer.id === "yes") {
        setResult(1);
      } else {
        answer.id === "no" ? setResult(1) : setResult(2);
      }
      setCurrentSection(SECTIONS.question5);
    }
    if (question.id === SECTIONS.question5)
      setCurrentSection(SECTIONS.question6);
    if (question.id === SECTIONS.question6) {
      switch (result) {
        case 1:
          setCurrentSection(SECTIONS.result1);
          break;
        case 2:
          setCurrentSection(SECTIONS.result2);
          break;
      }
    }
    
    const queId = question?.id?.charAt(question.id.length-1);
    let adobeLinkTitle = 'continue-'+queId
    trackAdobeClick(null, {
      linkTitle: `find my learning path:${adobeLinkTitle}`,
      linkModule: "kickoff-assessment",
      targetURL: null,
    });
  };

  const goBack = () => {
    let newStack = stack;
    const section = newStack.length === 0 ? SECTIONS.intro : newStack.pop();
    setStack(newStack);
    setCurrentSection(section);
  };

  useEffect(() => {
    if (
      (currentSection === SECTIONS.result1 ||
        currentSection === SECTIONS.result2) &&
      !isFinished
    ) {
      dispatch(userFinishedAssessmentThunk(answers));
      setIsFinished(true);
    }
  }, [currentSection, dispatch, SECTIONS, isFinished, answers]);

  useEffect(() => {
    if (affiliate.org && affiliate.sync === "yes") {
      const q = assessment.questions[4];
      onAnswer(q, { id: affiliate.org, title: affiliate.org });
    }
  }, []);

  const onAnswer = (question, answer) => {
    setAnswers({
      ...answers,
      [question.id]: { questionTitle: question.title, answer },
    });
  };

  // section = PMAgile | PMWaterfall
  const setSection = async (callToAction, section) => {
    trackAdobeClick(null, {
      linkTitle: callToAction,
      linkModule: `kickoff-assessment`,
      targetURL: `/app/activity/orientation`,
    });
    await dispatch(userSetsSectionThunk(section));
    history.push(`/app/activity/orientation`);
  };

  const buttonClass =
    "w-full bg-blue-400 rounded-lg rounded-tr-none uppercase font-bold text-center text-white tracking-wide font-secondary hover:bg-gray-800 hover:text-white px-10 py-3";

  if (!assessment) return <div />;

  const buttons = (
    <div className="flex justify-center mt-10">
      <div className="flex" style={{ width: "350px" }}>
        <button
          onClick={goBack}
          className="w-1/2 bg-color-main py-3 font-semibold rounded-tl-lg flex justify-between pl-5 pr-12 items-center text-lg border-r border-gray-400 shadow-lg"
        >
          <i className="fas fa-arrow-circle-left text-blue-400" />
          <span>BACK</span>
        </button>
        <button
          onClick={goNext}
          className="w-1/2 bg-color-main py-3 font-semibold rounded-tr-lg flex justify-between pr-5 pl-8 items-center text-lg border-l border-gray-400 shadow-lg"
        >
          CONTINUE
          <i className="fas fa-arrow-circle-right text-blue-400" />
        </button>
      </div>
    </div>
  );

  const intro = (
    <div className="flex-grow flex flex-col justify-center text-center">
      <div className="flex flex-col justify-end text-gray-800 py-12">
        <div className="font-semibold text-2xl py-32 px-24">
          {assessment.intro}
        </div>

        <div className="w-full md:w-1/2 lg:w-1/3 mx-auto">
          <button
            onClick={() => {
              trackAdobeClick(null, {
                linkTitle: "I'm ready, let's do it",
                linkModule: "kickoff-assessment",
                targetURL: "/app/activity/assessment",
              });
              setCurrentSection(SECTIONS.question1);
            }}
            className={buttonClass}
          >
            {assessment.introCallToAction}
          </button>
        </div>
      </div>
    </div>
  );

  const questionsComponents = assessment.questions.map((q) => (
    <div
      key={q.id}
      className="content-area flex-grow flex flex-col justify-center text-center px-15 sm:px-50 lg:px-15 py-10"
    >
      <div className="assessment-text text-gray-800 py-10 font-semibold text-2xl px-24">
        <p>{q.title}</p>
      </div>
      <div className="w-full md:w-1/2 lg:w-1/3 mx-auto">
        {q.options.map((i) => (
          <label
            key={i.id}
            className="block mb-2 w-full flex items-center cursor-pointer rounded border border-blue-200 py-1 px-3 text-left"
          >
            <input
              type="radio"
              name="answer"
              className="form-checkbox mr-3 uppercase"
              onChange={() => onAnswer(q, i)}
              checked={isSelected(q, i)}
            />
            <span className="uppercase">{i.title}</span>
          </label>
        ))}
      </div>

      {buttons}
    </div>
  ));

  const affiliationQuestion = () => {
    const q = assessment.questions[4];
    const answer = answers[q.id] && answers[q.id].answer;

    let option;
    if (affiliate.org && affiliate.sync === "yes") {
      option = { value: affiliate.org, label: affiliate.org };
    } else {
      option = answer ? { value: answer.id, label: answer.title } : undefined;
    }

    return (
      <div className="content-area flex-grow flex flex-col justify-center text-center px-15 sm:px-50 lg:px-15 py-10">
        <div className="assessment-text text-gray-800 py-10">
          <p>{q.title}</p>
        </div>
        <div className="w-full md:w-1/2 lg:w-1/3 mx-auto">
          <Select
            isClearable
            placeholder={"Type or select..."}
            options={_.sortBy(affiliates, ["label"])}
            onChange={(e) => {
              if (e) {
                onAnswer(q, { id: e.value, title: e.label });
              } else {
                setAnswers({
                  ...answers,
                  [getQuestionBySection().id]: null,
                });
              }
            }}
            onInputChange={(e, action) => {
              if (
                action.action !== "input-blur" &&
                action.action !== "menu-close"
              ) {
                setTempAffiliate(e);
              }
            }}
            inputValue={tempAffiliate}
            defaultValue={option}
            formatCreateLabel={(userInput) => `${t("Create")} ${userInput}`}
          />
        </div>

        {buttons}
      </div>
    );
  };

  const resultComponent = (html, callToAction, section) => {
    return (
      <div className="flex-grow flex flex-col justify-center text-center">
        <div className="flex flex-col justify-end text-gray-800 py-12">
          <div className="px-32 py-24">
            <div dangerouslySetInnerHTML={{ __html: html }} />
          </div>

          <div className="w-full md:w-1/2 lg:w-1/3 mx-auto">
            <button
              onClick={() => setSection(callToAction, section)}
              className={buttonClass}
            >
              {callToAction}
            </button>
          </div>

          <div className="w-full md:w-1/2 lg:w-1/3 mx-auto">
            <button onClick={startOver} className="text-blue-400 text-lg mt-5">
              {t("Start Over")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const result1 = resultComponent(
    assessment.waterfallResult,
    assessment.callToAction,
    "PMWaterfall"
  );

  const result2 = resultComponent(
    assessment.agileResult,
    assessment.callToAction,
    "PMAgile"
  );

  const progress = (100 * currentIndex()) / 7;

  return (
    <div
      id="assessment"
      adoberegion="kickoff-assessment"
      style={{ minHeight: "1000px" }}
    >
      <div className="lg:px-0 px-3">
        <div className="text-center pb-5">
          <div className="uppercase font-semibold text-3xl text-gray-900">
            {t("Find my learning path")}
          </div>

          <div>
            <div className="flex justify-center">
              <div className="ml-3 w-1/2 mt-2">
                <div
                  className="bg-blue-200 w-full rounded-lg"
                  style={{
                    height: "10px",
                  }}
                >
                  <div
                    className="rounded-lg bg-blue-600"
                    style={{
                      height: "10px",
                      width: `${progress}%`,
                      transition: "all .3s ease",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-xl rounded-lg">
          <div className="">
            {currentSection === SECTIONS.intro && intro}
            {currentSection === SECTIONS.question1 && questionsComponents[0]}
            {currentSection === SECTIONS.question2 && questionsComponents[1]}
            {currentSection === SECTIONS.question3 && questionsComponents[2]}
            {currentSection === SECTIONS.question4 && questionsComponents[3]}
            {currentSection === SECTIONS.question5 && affiliationQuestion()}
            {currentSection === SECTIONS.question6 && questionsComponents[5]}
            {currentSection === SECTIONS.result1 && result1}
            {currentSection === SECTIONS.result2 && result2}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assessment;
